import { useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useDebouncedValue } from '@/containers/DevTools/Feedback/useDebounceValue'
import {
  setSearch,
  setPage,
  toggleSort,
  setShowOnlyDeleted,
  setShowOnlyActive,
  setConfirmAction,
} from './additionalProductsToolsSlice'
import { useSnackbar } from 'baseui/snackbar'
import { additionalProductsToolsActions } from './additionalProductsToolsSagas'
import { selectAdditionalProductsToolsState } from './additionalProductsToolsSelectors'

export default function useAdditionalProductsTools() {
  const dispatch = useDispatch()
  const { enqueue } = useSnackbar()
  const state = useSelector(selectAdditionalProductsToolsState)
  const {
    items,
    search,
    page,
    pageSize,
    sortKey,
    sortDirection,
    showOnlyDeleted,
    showOnlyActive,
    confirmAction,
    isLoading,
    error,
    actionLoadingId,
    loaded,
  } = state

  const debouncedSearch = useDebouncedValue(search, 500)

  useEffect(() => {
    if (!loaded) {
      dispatch({ type: additionalProductsToolsActions.FETCH_LIST, enqueue })
    }
  }, [loaded])

  const filtered = useMemo(() => {
    let base = items
    if (showOnlyDeleted && !showOnlyActive) base = base.filter(p => p.deleted_at)
    if (showOnlyActive && !showOnlyDeleted) base = base.filter(p => !p.deleted_at)
    if (!debouncedSearch) return base
    const term = debouncedSearch.toLowerCase()
    return base.filter(p => [p.name, p.type, p.country, p.business_type, p.zoho_id].some(v => (v || '').toString().toLowerCase().includes(term)))
  }, [items, debouncedSearch, showOnlyDeleted, showOnlyActive])

  const sorted = useMemo(() => {
    const list = [...filtered]
    list.sort((a, b) => {
      const av = a[sortKey]
      const bv = b[sortKey]
      if (av == null && bv != null) return -1
      if (av != null && bv == null) return 1
      if (av == null && bv == null) return 0
      if (av < bv) return sortDirection === 'ASC' ? -1 : 1
      if (av > bv) return sortDirection === 'ASC' ? 1 : -1
      return 0
    })
    return list
  }, [filtered, sortKey, sortDirection])

  const numPages = Math.max(1, Math.ceil(sorted.length / pageSize))
  const pageSafe = Math.min(page, numPages)
  const paginated = sorted.slice((pageSafe - 1) * pageSize, pageSafe * pageSize)

  const handleDelete = product => {
    dispatch({ type: additionalProductsToolsActions.DELETE, id: product.id, name: product.name, enqueue })
  }

  const handleRestore = product => {
    dispatch({ type: additionalProductsToolsActions.RESTORE, id: product.id, name: product.name, enqueue })
  }

  return {
    search,
    page: pageSafe,
    sortKey,
    sortDirection,
    showOnlyDeleted,
    showOnlyActive,
    confirmAction,
    isLoading,
    error,
    actionLoadingId,
    debouncedSearch,
    paginated,
    filteredTotal: filtered.length,
    numPages,
    setSearch: value => dispatch(setSearch(value)),
    setPage: value => dispatch(setPage(value)),
    handleSort: key => dispatch(toggleSort(key)),
    toggleShowOnlyDeleted: () => dispatch(setShowOnlyDeleted()),
    toggleShowOnlyActive: () => dispatch(setShowOnlyActive()),
    openDeleteModal: product => dispatch(setConfirmAction({ type: 'delete', product })),
    openRestoreModal: product => dispatch(setConfirmAction({ type: 'restore', product })),
    closeModal: () => dispatch(setConfirmAction(null)),
    confirmActionAndExecute: () => {
      if (!confirmAction) return
      const { type, product } = confirmAction
      dispatch(setConfirmAction(null))
      if (type === 'delete') handleDelete(product)
      else handleRestore(product)
    },
    handleDelete,
    handleRestore,
  }
}
